
import React from "react";
import { Shield, Sword, Crown, Heart, Eye, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";

interface RpgPerceptiveSystemIconProps {
  diceValue: number;
  size?: "sm" | "md" | "lg";
  showLabel?: boolean;
  className?: string;
}

const getRpgSystemName = (diceValue: number): string => {
  switch (diceValue) {
    case 1: return "Mystic Listening";
    case 2: return "Body Mastery";
    case 3: return "Swift Movement";
    case 4: return "Mind Palace";
    case 5: return "World Sight";
    case 6: return "Soul Speaking";
    default: return "Unknown Arts";
  }
};

const RpgPerceptiveSystemIcon: React.FC<RpgPerceptiveSystemIconProps> = ({ 
  diceValue, 
  size = "md", 
  showLabel = false,
  className
}) => {
  const iconSizeClass = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-12 w-12",
  };

  const containerSizeClass = {
    sm: "w-10 h-10",
    md: "w-14 h-14",
    lg: "w-20 h-20",
  };

  const labelSizeClass = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  // Render RPG-themed icons based on the dice value
  const renderIcon = () => {
    switch (diceValue) {
      case 1:
        return <Shield className={iconSizeClass[size]} />;
      case 2:
        return <Heart className={iconSizeClass[size]} />;
      case 3:
        return <Sword className={iconSizeClass[size]} />;
      case 4:
        return <BookOpen className={iconSizeClass[size]} />;
      case 5:
        return <Eye className={iconSizeClass[size]} />;
      case 6:
        return <Crown className={iconSizeClass[size]} />;
      default:
        return <div className={iconSizeClass[size]}>?</div>;
    }
  };

  const getElementalColor = (diceValue: number): string => {
    switch (diceValue) {
      case 1: return "text-blue-400"; // Water - Listening
      case 2: return "text-red-400"; // Fire - Physical
      case 3: return "text-yellow-400"; // Lightning - Movement
      case 4: return "text-purple-400"; // Arcane - Thought
      case 5: return "text-green-400"; // Nature - Environment
      case 6: return "text-orange-400"; // Light - Social
      default: return "text-gray-400";
    }
  };

  return (
    <div className={cn("flex flex-col items-center", className)}>
      <div className={cn(
        containerSizeClass[size],
        "flex items-center justify-center rounded-full relative",
        "bg-gradient-to-br from-amber-900/30 to-amber-700/50",
        "border-2 border-amber-600/60 shadow-lg",
        getElementalColor(diceValue)
      )}>
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-transparent to-black/20" />
        {renderIcon()}
      </div>
      {showLabel && (
        <span className={cn(
          "mt-1 font-medium text-center text-amber-200",
          labelSizeClass[size]
        )}>
          {getRpgSystemName(diceValue)}
        </span>
      )}
    </div>
  );
};

export default RpgPerceptiveSystemIcon;
